import React, { useEffect, useState } from 'react';
import PageHeader from './components/PageHeader';
import { apiFetch } from './utils/api';

const DEFAULT_TEXT_COLOURS = [
  { value: '#000000', label: 'Black' },
  { value: '#444444', label: 'Dark grey' },
  { value: '#666666', label: 'Grey' },
  { value: '#999999', label: 'Light grey' },
  { value: '#ffffff', label: 'White' },
  { value: '#c00000', label: 'Dark red' },
  { value: '#ff0000', label: 'Red' },
  { value: '#ffc000', label: 'Gold' },
  { value: '#ffff00', label: 'Yellow' },
  { value: '#92d050', label: 'Light green' },
  { value: '#00b050', label: 'Green' },
  { value: '#00b0f0', label: 'Light blue' },
  { value: '#0070c0', label: 'Blue' },
  { value: '#002060', label: 'Navy' },
  { value: '#7030a0', label: 'Purple' },
  { value: '#e46c0a', label: 'Orange' },
];

const PDF_SAFE_FONTS = [
  { value: 'helvetica', label: 'Helvetica' },
  { value: 'times', label: 'Times' },
  { value: 'courier', label: 'Courier' },
];

const EMPTY_COLOUR = { value: '#111827', label: '' };

function normaliseColour(colour) {
  return {
    value: /^#[0-9a-f]{6}$/i.test(String(colour.value || '').trim()) ? colour.value : '#111827',
    label: String(colour.label || '').trim() || 'Colour',
  };
}

function validHexColour(value, fallback = '#111827') {
  const colour = String(value || '').trim();
  return /^#[0-9a-f]{6}$/i.test(colour) ? colour : fallback;
}

function SettingsDocumentDesign() {
  const [textColours, setTextColours] = useState(DEFAULT_TEXT_COLOURS);
  const [fontOptions, setFontOptions] = useState(PDF_SAFE_FONTS);
  const [newColour, setNewColour] = useState(EMPTY_COLOUR);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/company-settings/document-design');
      const data = await res.json();
      setTextColours(Array.isArray(data.text_colours) && data.text_colours.length ? data.text_colours : DEFAULT_TEXT_COLOURS);
      setFontOptions(Array.isArray(data.font_options) && data.font_options.length ? data.font_options : PDF_SAFE_FONTS);
    } catch (err) {
      setError(err.message || 'Failed to load document design settings.');
      setTextColours(DEFAULT_TEXT_COLOURS);
    } finally {
      setLoading(false);
    }
  }

  async function saveSettings(nextColours = textColours) {
    setSaving(true);
    setError('');
    setStatus('');
    try {
      const payload = {
        text_colours: nextColours.map(normaliseColour),
        font_options: fontOptions,
      };
      const res = await apiFetch('/api/company-settings/document-design', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save document design settings.');
      setTextColours(data.text_colours || payload.text_colours);
      setFontOptions(data.font_options || payload.font_options);
      setStatus('Document design settings saved.');
    } catch (err) {
      setError(err.message || 'Failed to save document design settings.');
    } finally {
      setSaving(false);
    }
  }

  function updateColour(index, patch) {
    setTextColours((current) => current.map((colour, colourIndex) => (
      colourIndex === index ? { ...colour, ...patch } : colour
    )));
  }

  function addColour() {
    const next = [...textColours, normaliseColour(newColour)];
    setTextColours(next);
    setNewColour(EMPTY_COLOUR);
  }

  function removeColour(index) {
    setTextColours((current) => current.filter((_, colourIndex) => colourIndex !== index));
  }

  function resetDefaults() {
    setTextColours(DEFAULT_TEXT_COLOURS);
    setFontOptions(PDF_SAFE_FONTS);
  }

  function toggleFont(font) {
    setFontOptions((current) => {
      if (current.some((item) => item.value === font.value)) {
        const next = current.filter((item) => item.value !== font.value);
        return next.length ? next : current;
      }
      return [...current, font];
    });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Document Design Settings"
        subtitle="Company-wide design options used by document and PDF template tools"
        backTo="/settings"
        backLabel="Settings"
        sticky
      />

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {(error || status) && (
          <div className={`rounded-lg border px-4 py-3 text-sm ${error ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
            {error || status}
          </div>
        )}

        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Text Colour Palette</h2>
              <p className="text-sm text-gray-500 mt-1">These colours appear in PDF overlay text controls for this company.</p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={resetDefaults}
                disabled={loading || saving}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => saveSettings()}
                disabled={loading || saving}
                className="rounded-md bg-[#082547] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0a2d5c] disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {textColours.map((colour, index) => (
              <div key={`${colour.value}-${index}`} className="rounded-md border border-gray-200 p-3">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={validHexColour(colour.value)}
                    onChange={(event) => updateColour(index, { value: event.target.value })}
                    className="h-9 w-11 rounded border border-gray-300 bg-white"
                    aria-label={`${colour.label} colour`}
                  />
                  <input
                    value={colour.label}
                    onChange={(event) => updateColour(index, { label: event.target.value })}
                    className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                    aria-label="Colour label"
                  />
                  <button
                    type="button"
                    onClick={() => removeColour(index)}
                    className="rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-100"
                  >
                    Remove
                  </button>
                </div>
                <input
                  value={colour.value}
                  onChange={(event) => updateColour(index, { value: event.target.value })}
                  className="mt-2 w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs uppercase"
                  aria-label="Colour hex value"
                />
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-md border border-gray-200 bg-gray-50 p-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Add Colour</h3>
            <div className="grid gap-3 sm:grid-cols-[auto_1fr_140px_auto] sm:items-end">
              <input
                type="color"
                value={validHexColour(newColour.value)}
                onChange={(event) => setNewColour((current) => ({ ...current, value: event.target.value }))}
                className="h-10 w-12 rounded border border-gray-300 bg-white"
                aria-label="New colour"
              />
              <label className="text-xs font-semibold text-gray-600">
                Label
                <input
                  value={newColour.label}
                  onChange={(event) => setNewColour((current) => ({ ...current, label: event.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              <label className="text-xs font-semibold text-gray-600">
                Hex
                <input
                  value={newColour.value}
                  onChange={(event) => setNewColour((current) => ({ ...current, value: event.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm uppercase"
                />
              </label>
              <button
                type="button"
                onClick={addColour}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Add
              </button>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900">PDF Overlay Fonts</h2>
          <p className="text-sm text-gray-500 mt-1 mb-4">These are built-in PDF-safe fonts available in the overlay designer.</p>
          <div className="grid gap-3 sm:grid-cols-3">
            {PDF_SAFE_FONTS.map((font) => {
              const enabled = fontOptions.some((item) => item.value === font.value);
              return (
                <label key={font.value} className="flex items-center gap-3 rounded-md border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={() => toggleFont(font)}
                  />
                  {font.label}
                </label>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

export default SettingsDocumentDesign;
